import Decimal from 'decimal.js';
import { equals } from 'ramda';

import { ItemInventory } from 'apis/inventory-management-service/types';
import { ItemConfiguration } from 'apis/picker-api/types';
import { DepositPermutation } from 'apis/restaurant-api/merchant-admin/deposits/types';
import {
  BulkChangeCategoryPayload,
  Category,
  DietaryPreference,
  Item,
  ItemCommission,
  ItemUpdatePayload,
  MandatoryWarning,
  MandatoryWarningCSVData,
  MonetaryAmount,
  ProductHierarchyLevel,
  SourceFieldConfig,
  Translation,
} from 'apis/restaurant-api/merchant-admin/menu2/types';
import { ItemPricingsUpdatePayload } from 'apis/restaurant-api/merchant-admin/merchant/types';
import { isDefined } from 'common/utils';
import { isOTCRestriction } from 'modules/menu/utils';

import getMaxCurrencyDecimals from '../../../components/getCurrencyMaxDecimals';
import { buildItemUpdateDataFromItem } from '../../../items/utils';
import { csvColumnToSourceFieldConfigMap } from '../../constants';
import { CSVColumnKey, PickingAreasItemSearchResult } from '../../types';
import { isAgeRestriction } from '../../utils';
import { ValidCSVData } from '../validations';

export type ItemInventoryPayload = { itemId: string; balance: number };

export type ItemCommissionsPayload = { item_id: string; commissions: ItemPricingsUpdatePayload };

export type PickingAreasPayload = { itemId: string; pickingAreaName: string; order: number | null };

export type CategoryOrderPayload = { categoryId: string; id: string; itemId: string; order: number | null };

type EditableItem = Pick<
  Item,
  | 'id'
  | 'barcode_gtin'
  | 'merchant_sku'
  | 'name'
  | 'brand_id'
  | 'description'
  | 'product_hierarchy_level_id'
  | 'caffeine_content'
  | 'baseprice'
  | 'discounted'
  | 'weight_in_grams'
  | 'volume_in_ml'
  | 'number_of_units'
  | 'display_unit'
  | 'vat_percentage'
  | 'alcohol_percentage'
  | 'external_id'
  | 'country_of_origin'
  | 'ingredients'
  | 'additives'
  | 'nutrition_facts'
  | 'allergens'
  | 'conditions_of_use_and_storage'
  | 'more_information'
  | 'distributor_information'
  | 'deposit_permutation_id'
  | 'producer_information'
  | 'enabled'
  | 'min_quantity_per_purchase'
  | 'max_quantity_per_purchase'
  | 'inventory_mode'
  | 'sell_by_weight_config'
  | 'restrictions'
  | 'image'
  | 'images'
  | 'mandatory_warnings'
  | 'dietary_preferences'
  | 'return_policy'
  | 'delivery_methods'
>;

const csvColumnToItemAttr: Record<CSVColumnKey, keyof EditableItem | null> = {
  item_id: 'id',
  gtin: 'barcode_gtin',
  merchant_sku: 'merchant_sku',
  name: 'name',
  brand_id: 'brand_id',
  description: 'description',
  caffeine_content_units: 'caffeine_content',
  caffeine_content_value: 'caffeine_content',
  product_hierarchy_id: 'product_hierarchy_level_id',
  product_hierarchy_name: null,
  price: 'baseprice',
  discounted_price: 'discounted',
  weight_in_grams: 'weight_in_grams',
  volume_in_ml: 'volume_in_ml',
  number_of_units: 'number_of_units',
  display_unit: 'display_unit',
  weighted_item_sell_by: 'sell_by_weight_config',
  weighted_item_approx_weight_in_grams: 'sell_by_weight_config',
  weighted_item_min_weight_in_grams: 'sell_by_weight_config',
  vat_percentage: 'vat_percentage',
  alcohol_percentage: 'alcohol_percentage',
  pos_id: 'external_id',
  country_of_origin: 'country_of_origin',
  ingredients: 'ingredients',
  additives: 'additives',
  nutrition_facts: 'nutrition_facts',
  allergens: 'allergens',
  conditions_of_use_and_storage: 'conditions_of_use_and_storage',
  more_information: 'more_information',
  distributor_information: 'distributor_information',
  producer_information: 'producer_information',
  enabled: 'enabled',
  is_over_the_counter: 'restrictions',
  min_quantity_per_purchase: 'min_quantity_per_purchase',
  max_quantity_per_purchase: 'max_quantity_per_purchase',
  use_limited_quantity: 'inventory_mode',
  use_inventory_management: 'inventory_mode',
  is_in_stock: 'inventory_mode',
  is_age_restricted_item: 'restrictions',
  delivery_handshake: 'restrictions',
  age_limit: 'restrictions',
  image_url: 'image',
  dietary_preferences: 'dietary_preferences',
  mandatory_warnings: 'mandatory_warnings',
  item_balance: null,
  location: null,
  commission_delivery: null,
  commission_pickup: null,
  category_id: null,
  category_name: null,
  category_item_sequence: null,
  picking_area: null,
  picking_sequence: null,
  deposit_value: 'deposit_permutation_id',
  deposit_vat: 'deposit_permutation_id',
  return_policy: 'return_policy',
  delivery_methods: 'delivery_methods',
};

const mandatoryWarningCsvDataToItemAttrsRecord: Record<MandatoryWarningCSVData, MandatoryWarning> = {
  alcohol_soft: 'with-alcohol-soft',
  alcohol_hard: 'with-alcohol-hard',
  choking_sausages: 'choking-sausages',
  choking_nuts: 'choking-nuts',
  sugar: 'with-sugar',
  fat: 'with-fat',
  sodium: 'with-sodium',
  otc: 'medicine-otc',
};

const dietaryPreferences: DietaryPreference[] = [
  'gluten-free',
  'halal',
  'kosher',
  'lactose-free',
  'vegan',
  'vegetarian',
];

const isDietaryPreferences = (value: any): value is DietaryPreference => dietaryPreferences.includes(value);

const deliveryMethods: DeliveryMethod[] = ['homedelivery', 'takeaway', 'eatin'];

const isDeliveryMethod = (value: any): value is DeliveryMethod => deliveryMethods.includes(value);

const updateTranslation = (language: Language, value: string, translations: Translation[]): Translation[] => [
  { lang: language, value, verified: true },
  ...translations.filter(({ lang }) => lang !== language),
];

const getMonetaryAmount = (value: number, currency: Currency): MonetaryAmount => ({
  currency,
  amount: value.toFixed(getMaxCurrencyDecimals(currency)),
});

const csvToItemAttrsTransformerFns: {
  [K in keyof EditableItem]: (
    csvData: ValidCSVData,
    settings: {
      language: Language;
      item: Item;
      productHierarchyLevels: ProductHierarchyLevel[];
      deposits: DepositPermutation[];
      selectedFields: CSVColumnKey[];
    },
  ) => Item[K];
} = {
  restrictions: (data, { item, selectedFields }) => {
    const itemAgeRestricted = item.restrictions.find(isAgeRestriction);
    const itemOtcRestricted = item.restrictions.find(isOTCRestriction);
    const itemDeliveryHandshakeRestricted = item.restrictions.find(({ type }) => type === 'delivery_handshake');
    const combinedRestrictions = [
      data.is_age_restricted_item === 'YES' || itemAgeRestricted
        ? {
            type: 'age_restricted' as const,
            age_limit: selectedFields.includes('age_limit')
              ? data.age_limit || null
              : itemAgeRestricted?.age_limit || null,
          }
        : undefined,
      data.is_over_the_counter === 'YES' || itemOtcRestricted ? { type: 'over_the_counter' as const } : undefined,
      data.delivery_handshake === 'YES' || itemDeliveryHandshakeRestricted
        ? { type: 'delivery_handshake' as const }
        : undefined,
    ].filter(isDefined);
    return combinedRestrictions.filter(
      r =>
        (r.type === 'age_restricted' && data.is_age_restricted_item !== 'NO') ||
        (r.type === 'over_the_counter' && data.is_over_the_counter !== 'NO') ||
        (r.type === 'delivery_handshake' && data.delivery_handshake !== 'NO'),
    );
  },
  id: (_, { item }) => item.id, // remains unchanged
  barcode_gtin: data => data.gtin || null,
  caffeine_content: data => {
    return data.caffeine_content_units && data.caffeine_content_value
      ? { value: data.caffeine_content_value, units: data.caffeine_content_units }
      : null;
  },
  merchant_sku: data => data.merchant_sku || null,
  name: (data, { language, item }) => {
    if (!data.name) {
      throw new Error('Column "name" is present, but undefined. Validation should not let this happen!');
    }
    return updateTranslation(language, data.name, item.name);
  },
  brand_id: data => (data.brand_id !== undefined && data.brand_id !== '' ? data.brand_id : null),
  description: (data, { language, item }) =>
    data.description ? updateTranslation(language, data.description, item.description) : [],
  product_hierarchy_level_id: (data, { productHierarchyLevels }) => {
    if (!data.product_hierarchy_id) {
      return null;
    }
    const level = productHierarchyLevels.find(l => l.human_readable_id === data.product_hierarchy_id);
    if (!level) {
      throw new Error(`Product hierarchy id "${data.product_hierarchy_id}" not found!`);
    }
    return level.id;
  },
  // The properties 'baseprice' and 'discounted' are dependent on each other and can't be mapped 1-to-1.
  // So they are later calculated via 'getBasepriceAndDiscounted'.
  baseprice: (_, { item }) => item.baseprice,
  discounted: (_, { item }) => item.discounted,
  weight_in_grams: data => data.weight_in_grams || null,
  volume_in_ml: data => data.volume_in_ml || null,
  number_of_units: data => data.number_of_units || null,
  display_unit: data => data.display_unit || null,
  vat_percentage: data => {
    if (data.vat_percentage === undefined) {
      throw new Error('Column "vat_percentage" is present, but undefined. Validation should not let this happen!');
    }
    return data.vat_percentage.toFixed(2);
  },
  alcohol_percentage: data => (data.alcohol_percentage ? new Decimal(data.alcohol_percentage).times(10).toNumber() : 0),
  external_id: data => data.pos_id || null,
  country_of_origin: (data, { language, item }) =>
    data.country_of_origin ? updateTranslation(language, data.country_of_origin, item.country_of_origin) : [],
  ingredients: (data, { language, item }) =>
    data.ingredients ? updateTranslation(language, data.ingredients, item.ingredients) : [],
  additives: (data, { language, item }) =>
    data.additives ? updateTranslation(language, data.additives, item.additives) : [],
  nutrition_facts: (data, { language, item }) =>
    data.nutrition_facts ? updateTranslation(language, data.nutrition_facts, item.nutrition_facts) : [],
  allergens: (data, { language, item }) =>
    data.allergens ? updateTranslation(language, data.allergens, item.allergens) : [],
  conditions_of_use_and_storage: (data, { language, item }) =>
    data.conditions_of_use_and_storage
      ? updateTranslation(language, data.conditions_of_use_and_storage, item.conditions_of_use_and_storage)
      : [],
  more_information: (data, { language, item }) =>
    data.more_information ? updateTranslation(language, data.more_information, item.more_information) : [],
  distributor_information: (data, { language, item }) =>
    data.distributor_information
      ? updateTranslation(language, data.distributor_information, item.distributor_information)
      : [],
  deposit_permutation_id: (data, { deposits }) =>
    deposits.find(d => Number(d.amount.amount) === data.deposit_value && Number(d.vat_percentage) === data.deposit_vat)
      ?.id ?? null,
  producer_information: (data, { language, item }) =>
    data.producer_information ? updateTranslation(language, data.producer_information, item.producer_information) : [],
  enabled: data => data.enabled === 'YES',
  min_quantity_per_purchase: data => data.min_quantity_per_purchase ?? null,
  max_quantity_per_purchase: data => data.max_quantity_per_purchase ?? null,
  inventory_mode: data => {
    if (data.is_in_stock === 'NO') {
      return 'forced_out_of_stock';
    }
    if (data.use_limited_quantity === 'YES') {
      return 'limited_quantity';
    }
    if (data.use_inventory_management === 'YES') {
      return 'inventory';
    }
    return null;
  },
  sell_by_weight_config: data => {
    if (data.weighted_item_sell_by === 'piece' && data.weighted_item_approx_weight_in_grams) {
      return {
        input_type: 'number_of_items',
        grams_per_step: data.weighted_item_approx_weight_in_grams,
      };
    }
    if (data.weighted_item_sell_by === 'weight' && data.weighted_item_min_weight_in_grams) {
      return {
        input_type: 'grams',
        grams_per_step: data.weighted_item_min_weight_in_grams,
      };
    }
    return null;
  },
  mandatory_warnings: data =>
    data.mandatory_warnings
      ?.split(' ')
      .map(mandatoryWarning => mandatoryWarningCsvDataToItemAttrsRecord[mandatoryWarning as MandatoryWarningCSVData])
      .filter(isDefined) ?? [],
  image: data => data.image_url ?? null,
  images: data => (data.image_url ? [{ url: data.image_url, blurhash: null }] : []),
  dietary_preferences: data => data.dietary_preferences?.split(' ').filter(isDietaryPreferences) ?? [],
  return_policy: data => data.return_policy ?? null,
  delivery_methods: data => data.delivery_methods?.split(' ').filter(isDeliveryMethod) ?? [],
};

const getBasepriceAndDiscounted = (
  originalItem: Item,
  price: number | undefined,
  discountedPrice: number | null | undefined,
): Partial<Pick<Item, 'baseprice' | 'discounted'>> => {
  const { currency } = originalItem.baseprice;
  if (price !== undefined) {
    // original price defined and discounted price undefined
    if (discountedPrice === undefined) {
      return originalItem.discounted
        ? {
            baseprice: originalItem.baseprice,
            discounted: { ...originalItem.discounted, original_price: getMonetaryAmount(price, currency) },
          }
        : { baseprice: getMonetaryAmount(price, currency), discounted: null };
    }
    // original price defined and discounted price defined
    if (typeof discountedPrice === 'number') {
      return originalItem.discounted
        ? {
            baseprice: getMonetaryAmount(discountedPrice, currency),
            discounted: { ...originalItem.discounted, original_price: getMonetaryAmount(price, currency) },
          }
        : {
            baseprice: getMonetaryAmount(discountedPrice, currency),
            discounted: { discount_type: 'absolute', original_price: getMonetaryAmount(price, currency) },
          };
    }
    // original price defined and discounted price null
    return { baseprice: getMonetaryAmount(price, currency), discounted: null };
  }
  // original price undefined and discounted price null
  if (discountedPrice === null) {
    return originalItem.discounted
      ? { baseprice: originalItem.discounted.original_price, discounted: null }
      : { baseprice: originalItem.baseprice, discounted: null };
  }
  // original price undefined and discounted price defined
  if (typeof discountedPrice === 'number') {
    return originalItem.discounted
      ? { baseprice: getMonetaryAmount(discountedPrice, currency), discounted: originalItem.discounted }
      : {
          baseprice: getMonetaryAmount(discountedPrice, currency),
          discounted: { discount_type: 'absolute', original_price: originalItem.baseprice },
        };
  }
  // original price undefined and discounted price undefined
  return {};
};

const cleanUpForWeightedItems = (isWeightedItem: boolean): Partial<Item> =>
  isWeightedItem
    ? {
        option_bindings: [],
        weight_in_grams: null,
        volume_in_ml: null,
        number_of_units: null,
        inventory_mode: null,
      }
    : {};

const updateImagesField = (imageAttr?: string | null) => {
  // Return undefined if image is not being updated at all
  if (imageAttr === undefined) {
    return;
  }

  // Add the image to the images array if image is of type string (a url)
  if (typeof imageAttr === 'string') {
    return {
      images: [{ url: imageAttr, blurhash: null }],
    };
  }

  // Empty the images array if image is being removed
  return {
    images: [],
  };
};

export const getItemUpdatePayload = (
  dataRow: ValidCSVData,
  originalItem: Item,
  settings: {
    selectedFields: CSVColumnKey[];
    language: Language;
    deposits?: DepositPermutation[];
    productHierarchyLevels: ProductHierarchyLevel[];
  },
): ItemUpdatePayload | undefined => {
  const { selectedFields, language, productHierarchyLevels, deposits = [] } = settings;

  const transformedItemData = selectedFields.reduce<Partial<Item>>((accCols, currCol) => {
    const itemKey = csvColumnToItemAttr[currCol];
    if (!itemKey) {
      return accCols;
    }
    const transformFn = csvToItemAttrsTransformerFns[itemKey];
    if (!transformFn) {
      return accCols;
    }
    return {
      ...accCols,
      [itemKey]: transformFn(dataRow, {
        language,
        item: originalItem,
        productHierarchyLevels,
        selectedFields,
        deposits,
      }),
    };
  }, {});

  const { price, discounted_price } = dataRow;
  const updateItemData: Partial<Item> = {
    ...transformedItemData,
    ...updateImagesField(transformedItemData.image),
    ...getBasepriceAndDiscounted(originalItem, price, discounted_price),
    ...cleanUpForWeightedItems(!!dataRow.weighted_item_sell_by),
  };

  const originalItemData = (Object.keys(updateItemData) as (keyof typeof updateItemData)[]).reduce<Partial<Item>>(
    (acc, curr) => {
      if (curr === 'images') {
        return { ...acc, images: originalItem.images.map(image => ({ blurhash: null, url: image.url })) };
      }
      return { ...acc, [curr]: originalItem[curr] };
    },
    {},
  );

  if (!equals(originalItemData, updateItemData)) {
    const itemPayload = buildItemUpdateDataFromItem(originalItem, updateItemData);
    if (!itemPayload.source_config) {
      return itemPayload;
    }
    const fieldOverrides = selectedFields.reduce<Partial<SourceFieldConfig>>((accFieldConfigs, currCsvField) => {
      const currSourceField = csvColumnToSourceFieldConfigMap[currCsvField];
      const itemKey = csvColumnToItemAttr[currCsvField];
      if (currSourceField !== null && itemKey !== null && !equals(originalItem[itemKey], itemPayload[itemKey])) {
        return { ...accFieldConfigs, [currSourceField]: 'USER_INPUT' };
      }
      return accFieldConfigs;
    }, {});
    return {
      ...itemPayload,
      source_config: {
        catalog_country: itemPayload.source_config.catalog_country,
        field_config: { ...itemPayload.source_config.field_config, ...fieldOverrides },
      },
    };
  }

  return undefined;
};

export const getInventoryUpdatePayload = (
  dataRow: ValidCSVData,
  originalItem: Item,
  settings: {
    selectedFields: CSVColumnKey[];
    inventory: ItemInventory[];
  },
): ItemInventoryPayload | undefined => {
  const { selectedFields, inventory } = settings;
  if (
    selectedFields.includes('item_balance') &&
    !dataRow.weighted_item_sell_by &&
    dataRow.item_balance !== undefined &&
    dataRow.item_balance !== inventory.find(item => item.itemId === originalItem.id)?.balance
  ) {
    return { itemId: originalItem.id, balance: dataRow.item_balance };
  }
  return undefined;
};

export const getItemConfigurationPayload = (
  dataRow: ValidCSVData,
  originalItem: Item,
  settings: {
    selectedFields: CSVColumnKey[];
    itemConfigs: ItemConfiguration[];
    menuId: string;
  },
): ItemConfiguration | undefined => {
  const { selectedFields, itemConfigs, menuId } = settings;
  const currLocation = itemConfigs.find(config => config.itemId === originalItem.id)?.storageLocation;
  if (selectedFields.includes('location') && currLocation !== dataRow.location) {
    return {
      itemId: originalItem.id,
      menuId,
      storageLocation: dataRow.location || null,
    };
  }
  return undefined;
};

export const getComissionsPayload = (
  dataRow: ValidCSVData,
  originalItem: Item,
  settings: {
    selectedFields: CSVColumnKey[];
    itemCommissions: ItemCommission[];
  },
): ItemCommissionsPayload | undefined => {
  const { selectedFields, itemCommissions } = settings;
  const existingItemPricings = itemCommissions.filter(p => p.item_id === originalItem.id);
  const originalDeliveryCommission =
    Number(existingItemPricings.find(p => p.delivery_method === 'homedelivery')?.value) ?? null;
  const originalTakeawayCommission =
    Number(existingItemPricings.find(p => p.delivery_method === 'takeaway')?.value) ?? null;
  const newDeliveryCommission =
    dataRow.commission_delivery !== undefined
      ? new Decimal(dataRow.commission_delivery).dividedBy(100).toNumber()
      : null;
  const newTakeawayCommission =
    dataRow.commission_pickup !== undefined ? new Decimal(dataRow.commission_pickup).dividedBy(100).toNumber() : null;
  if (
    (selectedFields.includes('commission_delivery') && newDeliveryCommission !== originalDeliveryCommission) ||
    (selectedFields.includes('commission_pickup') && newTakeawayCommission !== originalTakeawayCommission)
  ) {
    return {
      item_id: originalItem.id,
      commissions: {
        homedelivery: selectedFields.includes('commission_delivery')
          ? newDeliveryCommission
          : originalDeliveryCommission,
        takeaway: selectedFields.includes('commission_pickup') ? newTakeawayCommission : originalTakeawayCommission,
        eatin: Number(existingItemPricings.find(p => p.delivery_method === 'eatin')?.value) ?? null,
      },
    };
  }
  return undefined;
};

export const getPickingAreasPayload = (
  dataRow: ValidCSVData,
  originalItem: Item,
  settings: {
    selectedFields: CSVColumnKey[];
    pickingAreasItemSearchResult: PickingAreasItemSearchResult[];
  },
): PickingAreasPayload | undefined => {
  const { selectedFields, pickingAreasItemSearchResult } = settings;
  const currentPickingItem = pickingAreasItemSearchResult.find(item => item.itemId === originalItem.id);
  const { picking_area: newPickingArea, picking_sequence: newPickingSequence } = dataRow;

  const hasPickingAreaChanged =
    selectedFields.includes('picking_area') && currentPickingItem?.areaName !== newPickingArea;
  const hasPickingSequenceChanged =
    selectedFields.includes('picking_sequence') && currentPickingItem?.order !== newPickingSequence;

  const pickingAreaName = hasPickingAreaChanged ? newPickingArea : currentPickingItem?.areaName;

  const pickingSequence = hasPickingSequenceChanged ? newPickingSequence : currentPickingItem?.order;

  if (pickingAreaName && (hasPickingAreaChanged || hasPickingSequenceChanged)) {
    return {
      itemId: originalItem.id,
      pickingAreaName,
      order: pickingSequence || null,
    };
  }
  return undefined;
};

export const getCategoriesPayload = (
  dataRow: ValidCSVData,
  originalItem: Item,
  settings: {
    selectedFields: CSVColumnKey[];
    categories: Category[];
  },
): BulkChangeCategoryPayload | undefined => {
  const { selectedFields, categories } = settings;
  if (!selectedFields.includes('category_id') && !selectedFields.includes('category_name')) {
    return undefined;
  }
  const currentItemCategory = categories.find(category =>
    category.item_bindings.map(binding => binding.item_id).includes(originalItem.id),
  );
  const newItemCategory = selectedFields.includes('category_id')
    ? categories.find(category => category.id === dataRow.category_id)
    : selectedFields.includes('category_name')
      ? categories.find(category => category.name.some(name => name.value === dataRow.category_name))
      : null;

  if (currentItemCategory?.id === newItemCategory?.id) {
    return undefined;
  }
  return {
    category_ids: [],
    item_ids: [originalItem.id],
    destination_category_id: newItemCategory ? { $oid: newItemCategory.id } : null,
  };
};

export const getCategoryOrderPayload = (
  dataRow: ValidCSVData,
  originalItem: Item,
  settings: {
    selectedFields: CSVColumnKey[];
    categories: Category[];
  },
): CategoryOrderPayload | undefined => {
  const { selectedFields, categories } = settings;
  if (!selectedFields.includes('category_item_sequence')) {
    return undefined;
  }
  const category = categories.find(category =>
    category.item_bindings.find(binding => binding.item_id === originalItem.id),
  );
  if (!category) {
    return undefined;
  }
  const currentBindingIndex = category.item_bindings.findIndex(binding => binding.item_id === originalItem.id);
  const newItemSequence = dataRow.category_item_sequence;
  if (currentBindingIndex + 1 === newItemSequence) {
    return undefined;
  }
  return {
    categoryId: category.id,
    id: category.item_bindings[currentBindingIndex].id,
    itemId: originalItem.id,
    order: newItemSequence || null,
  };
};
